import React, { useMemo } from 'react';

import { FeaturePatternTrussContainer, LayoutFeatureArea, StepLayoutFeatureArea } from '@paragon/contract-library';
import { notNullish } from '@paragon/utilities';

import { useAppSelector } from '../../../../components/hooks';
import {
  calculateLayoutFeatureArea2DShape,
  calculateStepArea3DShape,
} from '../../../../library/layout-geometry/layoutFeatureAreas';
import { getActiveFeatureAreaMode } from '../../../../store/selectors/layoutVisualOptionsSelectors';
import { LayoutPolygonStyle } from '../../polygon-rendering/common/style';
import { LayoutPolygonCollection } from '../../polygon-rendering/LayoutPolygonCollection';
import { FeatureAreaEventHandlers } from './FeatureAreaEventHandlers';

interface Props {
  style: LayoutPolygonStyle;

  container: FeaturePatternTrussContainer;
  area: LayoutFeatureArea | Omit<LayoutFeatureArea, 'guid'>;
}

// As feature areas are currently always rendered at precisely the bottom elevation of their container, this adjustment
//   will prevent z-fighting with the container's bottom face.
const ELEVATION_ADJUSTMENT = 1 / 16;

export function GenericFeatureAreaVisual({ style, container, area }: Props) {
  const renderMode = useAppSelector(getActiveFeatureAreaMode);

  const bottomElevation = container.shape.bottomElevation + ELEVATION_ADJUSTMENT;

  const facePolygons = useMemo(
    () =>
      area.featureType === 'Step'
        ? // TODO: typescript
          calculateStepArea3DShape(area as Omit<StepLayoutFeatureArea, 'guid'>, bottomElevation)
        : [calculateLayoutFeatureArea2DShape(area)?.to3D(bottomElevation)].filter(notNullish),
    [area, bottomElevation],
  );

  if (facePolygons.length === 0 || renderMode === 'none') return null;

  return (
    <FeatureAreaEventHandlers area={area}>
      <LayoutPolygonCollection polygons={facePolygons} mode={renderMode} style={style} />
    </FeatureAreaEventHandlers>
  );
}
